import React, { useMemo } from 'react';
import { Table, TableColumnType, Tag, Button } from 'antd';
import dayjs from 'dayjs';
import { MemberTypeRecord } from '../types';
import { useAtomValue } from 'jotai';
import { isMobileAtom } from '@/jotai/atoms/userAtom';
import {
  getPaymentTypeText,
  getPeriodTypeText,
  getMemberLevelText,
  getStatusInfo,
} from '../utils';

interface MemberTypeTableProps {
  data: MemberTypeRecord[];
  loading: boolean;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number, pageSize?: number) => void;
  };
  onEdit: (record: MemberTypeRecord) => void;
}

const MemberTypeTable: React.FC<MemberTypeTableProps> = ({
  data,
  loading,
  pagination,
  onEdit,
}) => {
  const isMobile = useAtomValue(isMobileAtom);

  const columns = useMemo<TableColumnType<MemberTypeRecord>[]>(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 80,
      },
      {
        title: '会员等级',
        dataIndex: 'level',
        key: 'level',
        width: 100,
        render: (level: number, record: MemberTypeRecord) => {
          // level = 2 且 type = 1 (Google Pay) -> Basic
          // level = 2 且 type = 0 (Apple Pay) -> Lite
          // level = 3 且 type = 1 (Google Pay) -> Lite
          let text: string;
          if (level === 2) {
            if (record.type === 1) {
              text = 'Basic';
            } else {
              text = 'Lite';
            }
          } else if (level === 3 && record.type === 1) {
            text = 'Lite';
          } else {
            text = getMemberLevelText(level);
          }
          return <Tag>{text}</Tag>;
        },
      },
      {
        title: '类型',
        dataIndex: 'type',
        key: 'type',
        width: 120,
        render: (type: number) => {
          const text = getPaymentTypeText(type);
          return <Tag>{text}</Tag>;
        },
      },
      {
        title: '原价',
        dataIndex: 'originalAmount',
        key: 'originalAmount',
        width: 100,
        render: (amount: number) => amount?.toFixed(2),
      },
      {
        title: '现价',
        dataIndex: 'amount',
        key: 'amount',
        width: 100,
        render: (amount: number) => amount?.toFixed(2),
      },
      {
        title: '折扣',
        dataIndex: 'discount',
        key: 'discount',
        width: 100,
      },
      {
        title: '产品类型',
        dataIndex: 'periodType',
        key: 'periodType',
        width: 100,
        render: (type: number) => {
          const text = getPeriodTypeText(type);
          return <Tag>{text}</Tag>;
        },
      },
      {
        title: '商品id',
        dataIndex: 'productId',
        key: 'productId',
        width: 150,
      },
      {
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (status: number) => {
          const { text, color } = getStatusInfo(status);
          return <Tag color={color}>{text}</Tag>;
        },
      },
      {
        title: '创建时间',
        dataIndex: 'createTime',
        key: 'createTime',
        width: 180,
        render: (time: string) =>
          time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
      },
      {
        title: '更新时间',
        dataIndex: 'updateTime',
        key: 'updateTime',
        width: 180,
        render: (time: string) =>
          time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
      },
      {
        title: '更新人',
        dataIndex: 'updateUserName',
        key: 'updateUserName',
        width: 120,
        render: (name: string) => name || '-',
      },
      {
        title: '操作',
        key: 'action',
        width: 100,
        fixed: isMobile ? 'right' : undefined,
        render: (_, record) => (
          <Button type="link" onClick={() => onEdit(record)}>
            编辑
          </Button>
        ),
      },
    ],
    [isMobile, onEdit]
  );

  return (
    <Table<MemberTypeRecord>
      columns={columns}
      dataSource={data}
      rowKey="id"
      loading={loading}
      scroll={{ x: isMobile ? 1200 : undefined }}
      pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        onChange: pagination.onChange,
        onShowSizeChange: pagination.onChange,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total) => `共 ${total} 条`,
        pageSizeOptions: ['10', '20', '50', '100'],
      }}
      size={isMobile ? 'small' : 'middle'}
    />
  );
};

export default MemberTypeTable;

